import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import supabase from '../../lib/supabase';
import Modal from "../Common/Modal"
import { toast } from "sonner"
import Button from "../Common/Button"
import { useTranslation } from '../../utils/translations';

const { 
  FiPlus, FiMessageSquare, FiLoader, FiSave, FiX, FiUser, 
  FiClock, FiCheckCircle, FiHelpCircle, FiChevronDown, FiChevronUp,
  FiEdit, FiTrash2 // Added icons for edit and delete
} = FiIcons;

const ForumSection = ({ isAdmin, user }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [answeringId, setAnsweringId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [newQuestion, setNewQuestion] = useState({ title: '', content: '' });
  const [editingQuestionId, setEditingQuestionId] = useState(null); // New state for editing questions
  const [editingAnswerId, setEditingAnswerId] = useState(null); // New state for editing answers
  const [editQuestion, setEditQuestion] = useState({ title: '', content: '' }); // New state for editing question form
  const [editAnswerText, setEditAnswerText] = useState(''); // New state for editing answer form
  
  const locale = user?.language === 'fr' ? fr : enUS;
  const t = useTranslation(user?.language || 'fr'); // Default to French

  // Fetch questions
  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('forum_questions_a1b2c3d4e5')
          .select(`
            *,
            user:user_id (id, full_name, avatar_url),
            answerer:answered_by (id, full_name, avatar_url)
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setQuestions(data || []);
      } catch (err) {
        console.error('Error fetching questions:', err);
        toast.error(t('helpCenter.forumSection.failedToLoad', 'Failed to load questions. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, []);

  // Toggle question expansion
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Submit a new question
  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    if (!newQuestion.title.trim() || !newQuestion.content.trim()) {
      toast.error(t('helpCenter.forumSection.requiredFields', 'Title and content are required.'));
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('forum_questions_a1b2c3d4e5')
        .insert([{
          title: newQuestion.title,
          content: newQuestion.content,
          user_id: user.id
        }])
        .select(`
          *,
          user:user_id (id, full_name, avatar_url),
          answerer:answered_by (id, full_name, avatar_url)
        `);

      if (error) throw error;
      
      if (data && data.length > 0) {
        setQuestions([data[0], ...questions]);
      }

      toast.success(t('helpCenter.forumSection.submitSuccess', 'Your question has been submitted successfully.'));
      setNewQuestion({ title: '', content: '' });
      setIsCreating(false);
    } catch (err) {
      console.error('Error submitting question:', err);
      toast.error(t('helpCenter.forumSection.submitFailed', 'Failed to submit question. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Submit an answer to a question (admin only)
  const handleSubmitAnswer = async (e) => {
    e.preventDefault();
    
    if (!answerText.trim() || !answeringId) {
      toast.error(t('helpCenter.forumSection.answerRequired', 'Answer is required.'));
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('forum_questions_a1b2c3d4e5')
        .update({
          answer: answerText,
          answered_by: user.id,
          answered_at: new Date().toISOString(),
          is_answered: true
        })
        .eq('id', answeringId)
        .select(`
          *,
          user:user_id (id, full_name, avatar_url),
          answerer:answered_by (id, full_name, avatar_url)
        `);

      if (error) throw error;
      
      if (data && data.length > 0) {
        setQuestions(questions.map(q => q.id === answeringId ? data[0] : q));
      }

      toast.success(t('helpCenter.forumSection.answerSuccess', 'Answer submitted successfully.'));
      setAnswerText('');
      setAnsweringId(null);
    } catch (err) {
      console.error('Error submitting answer:', err);
      toast.error(t('helpCenter.forumSection.answerFailed', 'Failed to submit answer. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Edit a question
  const handleEditQuestion = async (e) => {
    e.preventDefault();

    if (!editQuestion.title.trim() || !editQuestion.content.trim()) {
      toast.error(t('helpCenter.forumSection.requiredFields', 'Title and content are required.'));
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('forum_questions_a1b2c3d4e5')
        .update({
          title: editQuestion.title,
          content: editQuestion.content,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingQuestionId)
        .select(`
          *,
          user:user_id (id, full_name, avatar_url),
          answerer:answered_by (id, full_name, avatar_url)
        `);

      if (error) throw error;

      if (data && data.length > 0) {
        setQuestions(questions.map(q => q.id === editingQuestionId ? data[0] : q));
      }

      toast.success(t('helpCenter.forumSection.editQuestionSuccess', 'Question updated successfully.'));
      setEditingQuestionId(null);
      setEditQuestion({ title: '', content: '' });
    } catch (err) {
      console.error('Error editing question:', err);
      toast.error(t('helpCenter.forumSection.editQuestionFailed', 'Failed to edit question. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Delete a question
  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm(t('helpCenter.forumSection.deleteQuestionConfirm', 'Are you sure you want to delete this question?'))) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('forum_questions_a1b2c3d4e5')
        .delete()
        .eq('id', questionId);

      if (error) throw error;

      setQuestions(questions.filter(q => q.id !== questionId));
      toast.success(t('helpCenter.forumSection.deleteQuestionSuccess', 'Question deleted successfully.'));
    } catch (err) {
      console.error('Error deleting question:', err);
      toast.error(t('helpCenter.forumSection.deleteQuestionFailed', 'Failed to delete question. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Edit an answer (admin only)
  const handleEditAnswer = async (e) => {
    e.preventDefault();

    if (!editAnswerText.trim()) {
      toast.error(t('helpCenter.forumSection.answerRequired', 'Answer is required.'));
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('forum_questions_a1b2c3d4e5')
        .update({
          answer: editAnswerText,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingAnswerId)
        .select(`
          *,
          user:user_id (id, full_name, avatar_url),
          answerer:answered_by (id, full_name, avatar_url)
        `);

      if (error) throw error;

      if (data && data.length > 0) {
        setQuestions(questions.map(q => q.id === editingAnswerId ? data[0] : q));
      }

      toast.success(t('helpCenter.forumSection.editAnswerSuccess', 'Answer updated successfully.'));
      setEditingAnswerId(null);
      setEditAnswerText('');
    } catch (err) {
      console.error('Error editing answer:', err);
      toast.error(t('helpCenter.forumSection.editAnswerFailed', 'Failed to edit answer. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Delete an answer (admin only)
  const handleDeleteAnswer = async (questionId) => {
    if (!window.confirm(t('helpCenter.forumSection.deleteAnswerConfirm', 'Are you sure you want to delete this answer?'))) return;

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('forum_questions_a1b2c3d4e5')
        .update({
          answer: null,
          answered_by: null,
          answered_at: null,
          is_answered: false
        })
        .eq('id', questionId)
        .select(`
          *,
          user:user_id (id, full_name, avatar_url),
          answerer:answered_by (id, full_name, avatar_url)
        `);

      if (error) throw error;

      if (data && data.length > 0) {
        setQuestions(questions.map(q => q.id === questionId ? data[0] : q));
      }

      toast.success(t('helpCenter.forumSection.deleteAnswerSuccess', 'Answer deleted successfully.'));
    } catch (err) {
      console.error('Error deleting answer:', err);
      toast.error(t('helpCenter.forumSection.deleteAnswerFailed', 'Failed to delete answer. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // Cancel operations
  const handleCancel = () => {
    if (isCreating) {
      setNewQuestion({ title: '', content: '' });
      setIsCreating(false);
    }
    
    if (answeringId) {
      setAnswerText('');
      setAnsweringId(null);
    }
    
    if (editingQuestionId) {
      setEditQuestion({ title: '', content: '' });
      setEditingQuestionId(null);
    }

    if (editingAnswerId) {
      setEditAnswerText('');
      setEditingAnswerId(null);
    }
  };

  if (loading && questions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('helpCenter.forumSection.title', 'Questions & Answers')}</h2>
        
        <Button
          onClick={() => setIsCreating(true)}
          icon={FiPlus}
          responsive
        >
          {t('helpCenter.forumSection.askQuestion', 'Ask a Question')}
        </Button>
      </div>

      {/* New Question Form */}
      <Modal isOpen={isCreating} title={t('helpCenter.forumSection.newQuestionTitle', 'Ask a New Question')} onClose={handleCancel}>
        <form onSubmit={handleSubmitQuestion} className="space-y-4">
          <div>
            <label htmlFor="question-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.forumSection.titleLabel', 'Title *')}
            </label>
            <input
              id="question-title"
              type="text"
              value={newQuestion.title}
              onChange={(e) => setNewQuestion({ ...newQuestion, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.forumSection.titlePlaceholder', 'Summarize your question')}
              required
            />
          </div>

          <div>
            <label htmlFor="question-content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.forumSection.contentLabel', 'Question Details *')}
            </label>
            <textarea
              id="question-content"
              value={newQuestion.content}
              onChange={(e) => setNewQuestion({ ...newQuestion, content: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.forumSection.contentPlaceholder', 'Provide details about your question')}
              rows={6}
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('helpCenter.forumSection.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              loading={saving}
              icon={FiSave}                            
            >
              {t('helpCenter.forumSection.submitQuestion', 'Submit Question')}          
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Question Form */}
      <Modal isOpen={!!editingQuestionId} title={t('helpCenter.forumSection.editQuestionTitle', 'Edit Question')} onClose={handleCancel}>
        <form onSubmit={handleEditQuestion} className="space-y-4">
          <div>
            <label htmlFor="edit-question-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.forumSection.titleLabel', 'Title *')}
            </label>
            <input
              id="edit-question-title"
              type="text"
              value={editQuestion.title}
              onChange={(e) => setEditQuestion({ ...editQuestion, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.forumSection.titlePlaceholder', 'Summarize your question')}
              required
            />
          </div>

          <div>
            <label htmlFor="edit-question-content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('helpCenter.forumSection.contentLabel', 'Question Details *')}
            </label>
            <textarea
              id="edit-question-content"
              value={editQuestion.content}
              onChange={(e) => setEditQuestion({ ...editQuestion, content: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder={t('helpCenter.forumSection.contentPlaceholder', 'Provide details about your question')}
              rows={6}
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('helpCenter.forumSection.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving}              
              loading={saving}
              icon={FiSave}
            >
              {t('helpCenter.forumSection.saveChanges', 'Save Changes')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Questions List */}
      <div className="space-y-4">
        {questions.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">{t('helpCenter.forumSection.noQuestions', 'No questions found. Be the first to ask a question!')}</p>
          </div>
        ) : (
          questions.map((question) => (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Question Header */}
              <div 
                className={`p-5 cursor-pointer ${question.is_answered ? 'border-b border-green-200 dark:border-green-800' : ''}`}
                onClick={() => toggleExpand(question.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`p-1.5 rounded-full ${question.is_answered ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'}`}>
                      <SafeIcon 
                        icon={question.is_answered ? FiCheckCircle : FiHelpCircle} 
                        className="w-4 h-4" 
                      />
                    </div>
                    <span className={`text-xs font-medium ${question.is_answered ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
                      {question.is_answered ? t('helpCenter.forumSection.statusAnswered', 'Answered') : t('helpCenter.forumSection.statusOpen', 'Open')}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Edit/Delete Buttons for Question */}
                    {(user.id === question.user_id || isAdmin) && (
                      <div className="flex space-x-2">
                        {user.id === question.user_id && !question.is_answered && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingQuestionId(question.id);
                              setEditQuestion({ title: question.title, content: question.content });
                            }}
                            className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                            icon={FiEdit}
                            iconClassName='w-4 h-4'
                          />
                        )}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteQuestion(question.id);
                          }}
                          className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                          icon={FiTrash2}
                          iconClassName='w-4 h-4'
                        />
                      </div>
                    )}
                    <SafeIcon 
                      icon={expandedId === question.id ? FiChevronUp : FiChevronDown} 
                      className="w-5 h-5 text-gray-400"
                    />
                  </div>
                </div>
                
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-2 pr-6">
                  {question.title}
                </h3>
                
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-3 space-x-4">
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiUser} className="w-3.5 h-3.5" />
                    <span>{question.user?.full_name || t('helpCenter.forumSection.unknownUser', 'Unknown User')}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
                    <span>
                      {formatDistanceToNow(new Date(question.created_at), { 
                        addSuffix: true,
                        locale: locale
                      })}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Question Content & Answer (when expanded) */}
              {expandedId === question.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-5 pb-5 pt-3"
                >
                  {/* Question Content */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-4">
                    {question.content}
                  </div>
                  
                  {/* Answer (if exists) */}
                  {question.is_answered && question.answer && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2 text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex items-center">
                          <span className="font-medium">{t('helpCenter.forumSection.answerFrom', 'Answer from:')}</span>
                          <span className="ml-1 text-blue-600 dark:text-blue-400">{question.answerer?.full_name || t('helpCenter.forumSection.admin', 'Admin')}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {/* Edit/Delete Buttons for Answer (Admin Only) */}
                          {isAdmin && (user.id === question.answered_by || isAdmin) && (
                            <>
                              <Button
                                onClick={() => {
                                  setEditingAnswerId(question.id);
                                  setEditAnswerText(question.answer);
                                }}
                                className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                                icon={FiEdit}
                                iconClassName='w-4 h-4'
                              />
                              <Button
                                onClick={() => handleDeleteAnswer(question.id)}
                                className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                                icon={FiTrash2}
                                iconClassName="w-4 h-4"
                              />
                            </>
                          )}
                        </div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800 p-4 rounded-lg text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {question.answer}
                      </div>
                    </div>
                  )}
                  
                  {/* Edit Answer Form (admin only) */}
                  {isAdmin && editingAnswerId === question.id && (
                    <Modal isOpen={editingAnswerId === question.id} title={t('helpCenter.forumSection.editAnswerTitle', 'Edit Answer')} onClose={handleCancel}>
                      <form onSubmit={handleEditAnswer} className="space-y-4">
                        <div>
                          <label htmlFor="edit-answer-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('helpCenter.forumSection.answerLabel', 'Your Answer *')}
                          </label>
                          <textarea
                            id="edit-answer-text"
                            value={editAnswerText}
                            onChange={(e) => setEditAnswerText(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder={t('helpCenter.forumSection.answerPlaceholder', 'Type your answer here...')}
                            rows={4}
                            required
                          />
                        </div>
                        <div className="flex justify-end space-x-3">
                          <Button
                            onClick={handleCancel}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            {t('helpCenter.forumSection.cancel', 'Cancel')}
                          </Button>
                          <Button
                            type="submit"
                            disabled={saving}
                            loading={saving}                            
                            icon={FiSave}
                          >
                            {t('helpCenter.forumSection.saveChanges', 'Save Changes')}
                          </Button>
                        </div>
                      </form>
                    </Modal>
                  )}
                  
                  {/* Answer Form (admin only, for unanswered questions) */}
                  {isAdmin && !question.is_answered && (
                    <div className="mt-4">
                      {answeringId === question.id ? (
                        <form onSubmit={handleSubmitAnswer} className="space-y-4">
                          <div>
                            <label htmlFor="answer-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('helpCenter.forumSection.answerLabel', 'Your Answer *')}
                            </label>
                            <textarea
                              id="answer-text"
                              value={answerText}
                              onChange={(e) => setAnswerText(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                              placeholder={t('helpCenter.forumSection.answerPlaceholder', 'Type your answer here...')}
                              rows={4}
                              required
                            />
                          </div>
                          <div className="flex justify-end space-x-3">
                            <Button
                              onClick={handleCancel}
                              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              {t('helpCenter.forumSection.cancel', 'Cancel')}
                            </Button>
                            <Button
                              type="submit"
                              disabled={saving}
                              loading={saving}
                              icon={FiSave}                              
                            >
                              {t('helpCenter.forumSection.submitAnswer', 'Submit Answer')}
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <Button
                          onClick={() => {
                            setAnsweringId(question.id);
                            setAnswerText('');
                          }}
                          icon={FiMessageSquare}
                        >
                          {t('helpCenter.forumSection.answerQuestion', 'Answer This Question')}
                        </Button>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default ForumSection;